import clsx from "clsx";
import type { ReactNode } from "react";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import type { DiffStatus } from "./diffLines";
import styles from "./DiffView.module.css";

export interface DiffRow {
  id: string;
  status: DiffStatus;
  /** What the line is: the material name (with its code), or the text line itself. */
  label: ReactNode;
  /** Value in the older version (amount, dilution); omitted for added lines. */
  before?: ReactNode;
  /** Value in the newer version; omitted for removed lines. */
  after?: ReactNode;
  /** Formatted change with its sign ("+5.00 %", "−0.120 g"). */
  delta?: ReactNode;
}

export interface DiffViewProps {
  rows: readonly DiffRow[];
  /** Version labels for the column headers ("v2", "v3"). */
  beforeLabel: string;
  afterLabel: string;
  labelHeader?: string;
  /** Collapse unchanged lines into a count. */
  hideUnchanged?: boolean;
  /** Describes the comparison for screen readers ("Changes from v2 to v3"). */
  caption: string;
  className?: string;
}

const MARKERS: Record<DiffStatus, { symbol: string; text: string }> = {
  added: { symbol: "+", text: "Added" },
  removed: { symbol: "\u2212", text: "Removed" },
  changed: { symbol: "~", text: "Changed" },
  unchanged: { symbol: "", text: "Unchanged" },
};

/**
 * Line-level differences between two formula versions. Status is shown by a marker symbol,
 * text for screen readers and color together; values sit in right-aligned numeric columns.
 */
export function DiffView({
  rows,
  beforeLabel,
  afterLabel,
  labelHeader = "Line",
  hideUnchanged = false,
  caption,
  className,
}: DiffViewProps) {
  const visible = hideUnchanged ? rows.filter((row) => row.status !== "unchanged") : rows;
  const hiddenCount = rows.length - visible.length;
  const showDelta = rows.some((row) => row.delta !== undefined);
  return (
    <div className={clsx(styles.wrapper, className)}>
      <table className={styles.table}>
        <caption className={styles.caption}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" className={styles.markerCell}>
              <VisuallyHidden>Change</VisuallyHidden>
            </th>
            <th scope="col">{labelHeader}</th>
            <th scope="col" className={styles.num}>
              {beforeLabel}
            </th>
            <th scope="col" className={styles.num}>
              {afterLabel}
            </th>
            {showDelta ? (
              <th scope="col" className={styles.num}>
                Change
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.id} className={styles.row} data-status={row.status}>
              <td className={styles.markerCell}>
                <span className={styles.marker} aria-hidden="true">
                  {MARKERS[row.status].symbol}
                </span>
                <VisuallyHidden>{MARKERS[row.status].text}</VisuallyHidden>
              </td>
              <td className={styles.label}>{row.label}</td>
              <td className={clsx(styles.num, styles.before)}>{row.status === "added" ? null : row.before}</td>
              <td className={clsx(styles.num, styles.after)}>{row.status === "removed" ? null : row.after}</td>
              {showDelta ? <td className={clsx(styles.num, styles.delta)}>{row.delta}</td> : null}
            </tr>
          ))}
          {visible.length === 0 ? (
            <tr>
              <td colSpan={showDelta ? 5 : 4} className={styles.note}>
                No differences between {beforeLabel} and {afterLabel}.
              </td>
            </tr>
          ) : null}
        </tbody>
        {hiddenCount > 0 ? (
          <tfoot>
            <tr>
              <td colSpan={showDelta ? 5 : 4} className={styles.note}>
                {hiddenCount} unchanged {hiddenCount === 1 ? "line" : "lines"} hidden
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
